import { useEffect, useState } from "react";
import { FileText, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminInvoiceSettings() {
  const [settings, setSettings] = useState({ sgst_percent: 9, cgst_percent: 9, auto_generate: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      if (r.data.invoice_settings) {
        setSettings(r.data.invoice_settings);
      }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { invoice_settings: settings });
      toast.success("Invoice settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings({ ...settings, [field]: value });
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl font-bold mb-1">Invoice Settings</h1>
      <p className="text-sm text-muted-foreground mb-6">Configure GST rates and invoice generation options.</p>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <FileText className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">GST Configuration</h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground">SGST (%)</label>
            <input 
              type="number" 
              min="0" 
              max="100" 
              step="0.1"
              value={settings.sgst_percent} 
              onChange={handleChange("sgst_percent")}
              data-testid="invoice-sgst"
              className="mt-1.5 w-full rounded-xl bg-secondary border border-input px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary" 
            />
            <p className="text-xs text-muted-foreground mt-1">State Goods and Services Tax</p>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">CGST (%)</label>
            <input 
              type="number" 
              min="0" 
              max="100" 
              step="0.1"
              value={settings.cgst_percent} 
              onChange={handleChange("cgst_percent")}
              data-testid="invoice-cgst"
              className="mt-1.5 w-full rounded-xl bg-secondary border border-input px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary" 
            />
            <p className="text-xs text-muted-foreground mt-1">Central Goods and Services Tax</p>
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center gap-3">
            <input 
              type="checkbox" 
              id="auto-generate" 
              checked={settings.auto_generate}
              onChange={handleChange("auto_generate")}
              data-testid="invoice-auto-generate"
              className="w-4 h-4 rounded border-input text-primary focus:ring-primary" 
            />
            <label htmlFor="auto-generate" className="text-sm font-medium text-foreground cursor-pointer">
              Auto-generate invoices when orders are confirmed
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2 ml-7">
            When enabled, invoices will be automatically generated when an order status changes to "Confirmed".
          </p>
        </div>

        <div className="bg-secondary/50 rounded-lg p-4">
          <p className="text-sm font-medium text-foreground mb-2">Current GST Rate</p>
          <p className="text-2xl font-mono font-bold text-primary">
            {(parseFloat(settings.sgst_percent) + parseFloat(settings.cgst_percent)).toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Total: SGST {settings.sgst_percent}% + CGST {settings.cgst_percent}%
          </p>
        </div>

        <button 
          onClick={save} 
          disabled={saving} 
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60"
          data-testid="invoice-settings-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 mt-5">
        <h2 className="font-semibold mb-3">Invoice Information</h2>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>• Invoices are stored securely in object storage</p>
          <p>• Users can download invoices from their account panel</p>
          <p>• Admins can download invoices from the orders section</p>
          <p>• Invoice PDFs include company logo, GST breakdown, and order details</p>
        </div>
      </div>
    </div>
  );
}
